<template>
  <div class="sale-details-wrapper">
    <button class="btn btn-light back-button-global" @click="goBack">
      <i class="bi bi-arrow-left"></i>
      <span>Retour</span>
    </button>
    <header class="page-header fade-in">
      <div>
        <h1>Détails de la vente</h1>
        <p v-if="saleData.reference">Référence {{ saleData.reference }}</p>
      </div>
      <div class="header-actions">
        <button
          v-if="normalizedSale.amountUnpaid > 0"
          class="btn btn-success"
          @click="openPaymentModal"
        >
          <i class="bi bi-cash-coin"></i>
          <span>Ajouter un paiement</span>
        </button>
        <button class="btn btn-secondary" @click="editSale">
          <i class="bi bi-pencil"></i>
          <span>Modifier</span>
        </button>
        <button class="btn btn-danger" @click="deleteSale">
          <i class="bi bi-trash"></i>
          <span>Supprimer</span>
        </button>
      </div>
    </header>

    <section v-if="!loading && hasSale" class="page-body fade-in">
      <div class="top-section">
        <div class="summary-card">
          <div class="summary-header">
            <h2>{{ normalizedSale.customerFullName || "Client inconnu" }}</h2>
          </div>
          <p class="summary-customer">
            {{
              normalizedSale.customerFullName ||
              normalizedSale.customerReference ||
              "Client inconnu"
            }}
          </p>
          <div class="summary-grid">
            <div>
              <span class="label">Montant</span>
              <span class="value">{{
                formatCurrency(normalizedSale.totalPriceTTC)
              }}</span>
            </div>
            <div>
              <span class="label">Quantité totale</span>
              <span class="value">{{
                formatQuantity(normalizedSale.totalQuantity)
              }}</span>
            </div>
            <div>
              <span class="label">Montant payé</span>
              <span class="value">{{
                formatCurrency(normalizedSale.amountPaid)
              }}</span>
            </div>
            <div>
              <span class="label">Restant dû</span>
              <span class="value">{{
                formatCurrency(normalizedSale.amountUnpaid)
              }}</span>
            </div>
            <div>
              <span class="label">Date vente</span>
              <span class="value">{{ formatDate(normalizedSale.date) }}</span>
            </div>
            <div>
              <span class="label">Dernière mise à jour</span>
              <span class="value">{{
                formatDate(normalizedSale.updated_at)
              }}</span>
            </div>
          </div>
        </div>

        <div class="notes-card" v-if="normalizedSale.remark">
          <h3>Remarque</h3>
          <p>{{ normalizedSale.remark }}</p>
        </div>
      </div>

      <div class="info-grid">
        <div class="info-card">
          <h3>Informations client</h3>
          <div class="info-line">
            <span class="label">Client</span>
            <span class="value">{{
              normalizedSale.customerFullName || "—"
            }}</span>
          </div>
          <div class="info-line" v-if="normalizedSale.customerReference">
            <span class="label">Référence client</span>
            <span class="value">{{ normalizedSale.customerReference }}</span>
          </div>
        </div>
      </div>

      <div
        class="items-section"
        v-if="normalizedSale.items && normalizedSale.items.length > 0"
      >
        <h3>Articles</h3>
        <div class="items-table">
          <div class="table-header">
            <div class="col-reference">Référence</div>
            <div class="col-name">Nom</div>
            <div class="col-quantity">Quantité</div>
            <div class="col-price">Prix unitaire</div>
            <div class="col-total">Total</div>
          </div>
          <div
            v-for="(item, index) in normalizedSale.items"
            :key="index"
            class="table-row"
          >
            <div class="col-reference">{{ item.reference || "—" }}</div>
            <div class="col-name">{{ item.name || "—" }}</div>
            <div class="col-quantity">{{ formatQuantity(item.quantity) }}</div>
            <div class="col-price">{{ formatCurrency(item.price) }}</div>
            <div class="col-total">{{ formatCurrency(item.total) }}</div>
          </div>
        </div>
      </div>
    </section>

    <div v-else-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Chargement de la vente...</p>
    </div>

    <div v-else class="empty-state">
      <i class="bi bi-emoji-frown"></i>
      <h3>Vente introuvable</h3>
      <p>La vente demandée semble avoir été supprimée.</p>
      <button class="btn btn-primary" @click="goToList">
        <i class="bi bi-cash-stack"></i>
        <span>Voir les ventes</span>
      </button>
    </div>

    <!-- Payment Modal -->
    <div
      v-if="showPaymentModal"
      class="modal-overlay"
      @click.self="closePaymentModal"
    >
      <div class="payment-modal">
        <div class="payment-modal-header">
          <h3>
            <i class="bi bi-cash-coin"></i>
            Ajouter un paiement
          </h3>
          <button class="modal-close-btn" @click="closePaymentModal">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <div class="payment-modal-body">
          <div class="payment-info">
            <div class="info-row">
              <span class="info-label">Montant restant:</span>
              <span class="info-value total-amount">
                {{ formatCurrency(normalizedSale.amountUnpaid) }}
              </span>
            </div>
          </div>

          <div class="payment-form">
            <div class="form-group">
              <label>Montant reçu <span class="required">*</span></label>
              <div class="amount-buttons">
                <button
                  v-for="amount in [200, 100, 50, 20, 10]"
                  :key="amount"
                  type="button"
                  class="amount-btn"
                  :class="{
                    active: paymentForm.selectedAmounts.includes(amount),
                  }"
                  @click="toggleAmount(amount)"
                >
                  {{ amount }} DH
                </button>
              </div>
              <input
                v-model.number="paymentForm.amountReceived"
                type="number"
                step="0.01"
                class="form-input"
                placeholder="0.00"
                @input="calculateChange"
              />
            </div>

            <div v-if="paymentForm.change > 0" class="change-display">
              <div class="change-label">Retour au client:</div>
              <div class="change-amount">
                {{ formatCurrency(paymentForm.change) }}
              </div>
            </div>

            <div class="form-group">
              <label>Date <span class="required">*</span></label>
              <input
                v-model="paymentForm.date"
                type="date"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <div class="payment-modal-footer">
          <button class="btn-cancel" @click="closePaymentModal">Annuler</button>
          <button
            class="btn-confirm"
            @click="addPayment"
            :disabled="
              !paymentForm.amountReceived ||
              paymentForm.amountReceived <= 0 ||
              paymentForm.amountReceived < normalizedSale.amountUnpaid ||
              addingPayment
            "
          >
            <i v-if="!addingPayment" class="bi bi-check-circle"></i>
            <span v-else class="spinner-small"></span>
            {{
              addingPayment ? "Enregistrement..." : "Enregistrer le paiement"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "SaleShow",
  data() {
    return {
      loading: true,
      showPaymentModal: false,
      addingPayment: false,
      paymentForm: {
        amount: 0,
        amountReceived: 0,
        change: 0,
        selectedAmounts: [],
        date: new Date().toISOString().split("T")[0],
      },
    };
  },
  computed: {
    ...mapGetters("sale", {
      sale: "getSale",
    }),
    reference() {
      return this.$route.params.reference;
    },
    saleData() {
      return this.sale || {};
    },
    hasSale() {
      return Object.keys(this.saleData).length > 0;
    },
    normalizedSale() {
      const toNumber = (value, fallback = 0) => {
        if (value === null || value === undefined || value === "") {
          return fallback;
        }
        const number = Number(value);
        return Number.isNaN(number) ? fallback : number;
      };

      if (!this.hasSale) {
        return {
          items: [],
        };
      }

      const rawCustomer =
        this.saleData.seller_customer || this.saleData.customer || {};

      const items = Array.isArray(this.saleData.sale_items)
        ? this.saleData.sale_items
        : Array.isArray(this.saleData.items)
        ? this.saleData.items
        : [];

      const totalPriceTTC = toNumber(
        this.saleData.totalPriceTTC != null
          ? this.saleData.totalPriceTTC
          : this.saleData.total_price_ttc != null
          ? this.saleData.total_price_ttc
          : this.saleData.total_amount != null
          ? this.saleData.total_amount
          : this.saleData.total,
        0
      );
      const totalQuantity = toNumber(
        this.saleData.totalQuantity != null
          ? this.saleData.totalQuantity
          : this.saleData.total_quantity,
        0
      );
      const amountPaid = toNumber(
        this.saleData.amountPaid != null
          ? this.saleData.amountPaid
          : this.saleData.amount_paid != null
          ? this.saleData.amount_paid
          : this.saleData.payrollAmount,
        0
      );

      const amountUnpaid = toNumber(
        this.saleData.amountUnpaid != null
          ? this.saleData.amountUnpaid
          : this.saleData.amount_unpaid != null
          ? this.saleData.amount_unpaid
          : this.saleData.remaining_amount != null
          ? this.saleData.remaining_amount
          : totalPriceTTC - amountPaid,
        0
      );

      return {
        id: this.saleData.id,
        reference: this.saleData.reference || this.reference,
        customerFullName:
          rawCustomer.fullName ||
          rawCustomer.full_name ||
          this.saleData.customerFullName ||
          this.saleData.customer_full_name ||
          this.saleData.customer_name ||
          null,
        customerReference:
          this.saleData.customer_reference ||
          this.saleData.customer_reference ||
          rawCustomer.reference ||
          null,
        date:
          this.saleData.date ||
          this.saleData.sale_date ||
          this.saleData.created_at ||
          null,
        totalPriceTTC,
        totalQuantity,
        amountPaid,
        amountUnpaid,
        remark: this.saleData.remark || this.saleData.notes || null,
        updated_at: this.saleData.updated_at || null,
        items: items.map((item) => {
          const itemData = item.item || {};
          const quantity = toNumber(
            item.quantity != null ? item.quantity : item.qty,
            0
          );
          const price = toNumber(
            item.priceTTC != null
              ? item.priceTTC
              : item.price_ttc != null
              ? item.price_ttc
              : item.price != null
              ? item.price
              : itemData.priceTTC || itemData.price_ttc || itemData.price,
            0
          );
          return {
            reference:
              item.item_reference ||
              itemData.reference ||
              item.reference ||
              "—",
            name:
              item.fullName ||
              itemData.fullName ||
              itemData.name ||
              itemData.designation ||
              "—",
            quantity,
            price,
            total: quantity * price,
          };
        }),
      };
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    goToList() {
      this.$router.push({ name: "sales-list" });
    },
    editSale() {
      this.$router.push({
        name: "sales-edit",
        params: { reference: this.reference },
      });
    },
    async deleteSale() {
      try {
        await this.$store.dispatch("sale/destroy", this.reference);
        this.$toast.success("Vente supprimée avec succès.");
        this.goToList();
      } catch (error) {
        console.error("Error deleting sale", error);
        this.$toast.error("Impossible de supprimer la vente.");
      }
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatQuantity(value) {
      if (!value && value !== 0) return "—";
      const number = Number(value);
      if (Number.isNaN(number)) return value;
      return number % 1 === 0 ? number.toString() : number.toFixed(2);
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString();
      } catch (error) {
        return value;
      }
    },
    async loadSale() {
      this.loading = true;
      try {
        await this.$store.dispatch("sale/show", this.reference);
      } catch (error) {
        console.error("Error fetching sale", error);
        this.$toast.error("Impossible de récupérer la vente.");
      } finally {
        this.loading = false;
      }
    },
    openPaymentModal() {
      // Set default payment amount to amountUnpaid
      this.paymentForm.amount = this.normalizedSale.amountUnpaid;
      this.paymentForm.amountReceived = this.normalizedSale.amountUnpaid;
      this.paymentForm.change = 0;
      this.paymentForm.selectedAmounts = [];
      this.paymentForm.date = new Date().toISOString().split("T")[0];
      this.showPaymentModal = true;
    },
    closePaymentModal() {
      this.showPaymentModal = false;
      this.paymentForm = {
        amount: 0,
        amountReceived: 0,
        change: 0,
        selectedAmounts: [],
        date: new Date().toISOString().split("T")[0],
      };
    },
    toggleAmount(amount) {
      const index = this.paymentForm.selectedAmounts.indexOf(amount);
      if (index > -1) {
        // Remove if already selected
        this.paymentForm.selectedAmounts.splice(index, 1);
      } else {
        // Add if not selected
        this.paymentForm.selectedAmounts.push(amount);
      }
      // Calculate total from selected amounts
      const totalSelected = this.paymentForm.selectedAmounts.reduce(
        (sum, amt) => sum + amt,
        0
      );
      this.paymentForm.amountReceived = totalSelected;
      this.calculateChange();
    },
    calculateChange() {
      const received = parseFloat(this.paymentForm.amountReceived || 0);
      const unpaid = parseFloat(this.normalizedSale.amountUnpaid || 0);
      this.paymentForm.change = Math.max(0, received - unpaid);
      // Set payment amount to unpaid amount (not the received amount)
      this.paymentForm.amount = unpaid;
    },
    async addPayment() {
      if (
        !this.paymentForm.amountReceived ||
        this.paymentForm.amountReceived <= 0
      ) {
        this.$toast?.error?.("Veuillez saisir le montant reçu");
        return;
      }

      if (this.paymentForm.amountReceived < this.normalizedSale.amountUnpaid) {
        this.$toast?.error?.(
          "Le montant reçu doit être supérieur ou égal au montant restant"
        );
        return;
      }

      this.addingPayment = true;
      try {
        const paymentData = {
          sale_reference: this.normalizedSale.reference,
          customer_reference:
            this.normalizedSale.customerReference || "particulier",
          amount: this.paymentForm.amount, // Amount to pay (unpaid amount)
          date: this.paymentForm.date,
          paymentMethod: "espece",
          remark: `Paiement espece pour la vente ${
            this.normalizedSale.reference
          }${
            this.paymentForm.change > 0
              ? ` - Retour: ${this.paymentForm.change.toFixed(2)} MAD`
              : ""
          }`,
        };

        // Use payments API for sale payments
        await this.$api.create("/api/payments", paymentData);
        this.$toast?.success?.(
          this.paymentForm.change > 0
            ? `Paiement enregistré! Retour: ${this.formatCurrency(
                this.paymentForm.change
              )}`
            : "Paiement enregistré avec succès!"
        );
        this.closePaymentModal();
        // Reload sale to update amounts
        await this.loadSale();
      } catch (paymentError) {
        console.error("Error creating payment:", paymentError);
        this.$toast?.error?.("Impossible d'enregistrer le paiement");
      } finally {
        this.addingPayment = false;
      }
    },
  },
  mounted() {
    this.loadSale();
  },
};
</script>

<style scoped>
.sale-details-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.back-button-global {
  margin-bottom: -1rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.top-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-card {
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.08),
    rgba(147, 51, 234, 0.08)
  );
  border-radius: 20px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.summary-header h2 {
  margin: 0;
  font-size: 1.6rem;
  color: #0f172a;
}

.summary-customer {
  margin: 0;
  color: #64748b;
  font-size: 1rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.summary-grid .label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.summary-grid .value {
  display: block;
  font-weight: 600;
  color: #0f172a;
  font-size: 1.1rem;
}

.notes-card {
  background: #f9f9ff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 12px 24px rgba(99, 102, 241, 0.06);
}

.notes-card h3 {
  margin: 0 0 0.75rem;
  font-size: 1.2rem;
  color: #0f172a;
}

.notes-card p {
  margin: 0;
  color: #475569;
  line-height: 1.6;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
}

.info-card {
  background: #f9f9ff;
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 12px 24px rgba(99, 102, 241, 0.06);
}

.info-card h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #0f172a;
}

.info-line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  color: #475569;
}

.info-line .label {
  font-weight: 600;
  color: #64748b;
}

.info-line .value {
  font-weight: 600;
  color: #111827;
}

.items-section {
  margin-top: 1rem;
}

.items-section h3 {
  margin: 0 0 1rem;
  font-size: 1.3rem;
  color: #0f172a;
}

.items-table {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s ease;
}

.table-row:hover {
  background: #f9fafb;
}

.table-row:last-child {
  border-bottom: none;
}

.col-reference,
.col-name,
.col-quantity,
.col-price,
.col-total {
  display: flex;
  align-items: center;
  color: #1f2937;
}

.col-name {
  font-weight: 600;
}

.col-total {
  font-weight: 700;
  color: #2563eb;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #2563eb;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-secondary {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .sale-details-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  .table-header {
    display: none;
  }
  .table-row {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    margin-bottom: 0.75rem;
    padding: 1rem;
  }
  .col-reference::before {
    content: "Référence: ";
    font-weight: 600;
    color: #64748b;
  }
  .col-name::before {
    content: "Nom: ";
    font-weight: 600;
    color: #64748b;
  }
  .col-quantity::before {
    content: "Quantité: ";
    font-weight: 600;
    color: #64748b;
  }
  .col-price::before {
    content: "Prix: ";
    font-weight: 600;
    color: #64748b;
  }
  .col-total::before {
    content: "Total: ";
    font-weight: 600;
    color: #64748b;
  }
}

@media (max-width: 768px) {
  .sale-details-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .summary-card {
    padding: 1.25rem;
  }
}

.btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 10px 22px rgba(16, 185, 129, 0.25);
}

/* Payment Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.payment-modal {
  background: #ffffff;
  border-radius: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  height: auto;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.payment-modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
}

.payment-modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.payment-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.payment-info {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.info-label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}

.info-value {
  font-size: 1rem;
  color: #1a202c;
  font-weight: 600;
}

.info-value.total-amount {
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.payment-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  overflow: hidden;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568;
}

.required {
  color: #ef4444;
}

.form-input {
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.amount-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.amount-btn {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #4a5568;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  flex: 1;
  min-width: 70px;
}

.amount-btn:hover {
  border-color: #667eea;
  background: #f0f4ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.amount-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
}

.change-display {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem 0;
  animation: slideIn 0.3s ease;
  flex-shrink: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.change-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
}

.change-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
}

.payment-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  background: #f8f9fa;
  flex-shrink: 0;
}

.btn-cancel,
.btn-confirm {
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
  border: none;
}

.btn-cancel {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-cancel:hover {
  background: #cbd5e0;
}

.btn-confirm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-confirm:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
</style>
